import Backbone from 'backbone'
import $ from 'jquery'
import _ from 'lodash'
import deviceType from 'scoreboard/components/device-type'
import BusyButton from 'scoreboard/components/busy-button'

/**
 * View is for list/trees that require clicking a select button in
 * a node in order to select/add the node. (example: org-pane)
 *
 * Usage:
 *
 *	this.hoverActions = new HoverActions({
 *		el: this.el // Attach to the view
 *		buttonContent: i18n.get('common.add'), // Set the button text (e.g. Add)
 *		nodesReselectable: true // Optional - Allows nodes to be selected more than once
 *	}).render()
 *
 * @fires buttonContentSet Triggered when the content of the select button is
 * updated. Returns the the DOM element representing the node for which the
 * button content is updated..
 *
 * @fires nodeActivated Triggered when a node is designated as "active."
 * Passes the DOM element representing the active node.
 *
 */

var HoverActions = Backbone.View.extend({

	events: {
		'mouseenter .node': 'setButtonContent',
		'touchstart .node': 'setButtonContent', // For mobile
		'click .node': 'nodeFocused',
		'mouseleave .node': 'buttonFullOpacity',
		'mouseenter .node .label-mask': 'onMouseEnterLabelMask',
		'mouseleave .node .label-mask': 'allowDrag'
	},

	initialize: function(options) {
		this.options = options || {}

		// provides a way to check if the node itself should be addable, defaults
		// to true if no function was defined
		this.addable = this.options.addable || _.stubTrue
	},

	render: function(e) {
		this.$el.addClass('tree-actions')
		// If node is preselected, add its button text.
		this.setButtonContent()
		// If node is preselected, set its button at full opacity.
		this.buttonFullOpacity()

		this.delegateClickHandler()

		return this
	},

	setButtonContent: function(e, nodeEl, preventTrigger) {
		// Set the select-button content in a data-attribute so the label-mask:before
		// can access and display it via the `content` property
		let node = nodeEl?.length ?	nodeEl :
			e?.currentTarget ? $(e.currentTarget) : this.$('.node.selected')
		if (!preventTrigger) {
			this.trigger('buttonContentSet', node)
		}
		var addable = this.addable(node)
		node.toggleClass('addable', addable)
		if (addable) {
			node.find('.label-mask').attr('data-button', this.options.buttonContent)
		}
	},

	// Give the selected node's button full opacity and
	// remove other node tree-action buttons if they exist
	buttonFullOpacity: function() {
		// Only run if a selected node exist. There is potential for this
		// method to be called without a selected node returned (ie: search results)
		if (this.$('.selected')) {
			// If mouse is no longer hovering nodes, we want the selected node's
			// select button to have full opacity
			this.$('.selected .label-mask').addClass('is-selected')
			this.$('.node:not(.selected) .label-mask').removeClass('is-selected')
			this.delegateClickHandler()
			// Wrapped in `nextTick` to ensure the `nodeSelected` event is
			// triggered when a user clicks the browser's back button.
			process.nextTick(() => {
				this.trigger('nodeSelected', this.$('.selected'))
			})
		}
	},

	/**
	 * Clears the UI state (appropriate when the tree selection has been cleared)
	 */
	clear: function () {
		this.buttonFullOpacity()
	},

	onSearch: function() {
		this.setButtonContent()
		this.buttonFullOpacity()
	},

	/**
	 * Allows programmatic initialization of classes that show the tree action contents properly.  Typically these classes
	 * are toggle through the UI via events, but in the case where a tree node is selected programmatically we need to
	 * call this method to ensure the correct classes are applied to that new node selection
	 */
	applyExistingSelection(selected) {
		if (_.get(selected, 'length', 0) == 0) return
		this.setButtonContent()
		this.buttonFullOpacity()
		this.activeNode(selected)
	},

	nodeFocused: function() {
		this.buttonFullOpacity()
	},

	delegateClickHandler: function () {
		this.$('.label-mask').off('click.tree-actions')
		// If on touchdevice, we don't want the user to be able to tap on the right side where a button will be
		// and have it choose the item. Instead, if its tapped, it should be like tapping anywhere on the row
		// and have the button show up as an option to be selected.
		var active = deviceType.isMobile() ? this.$('.selected .label-mask.is-selected') : this.$('.label-mask.is-selected')
		active.on('click.tree-actions', this.select.bind(this))
	},

	// Determine if a displayed button should have full opacity or not.
	// If a there is a selected node but the cursor is over the button area of
	// another node, the selected node's opacity lowers and the hovered node's
	// button receives full opacity.
	onMouseEnterLabelMask: function(e) {
		let tree = this.$('.tree')
		if (tree?.hasClass('editable')) {
			tree.removeClass('editable').addClass('editable-no-drag')
		}
		var labelMask = this.$('.node.selected').find('.label-mask')
		var target = $(e.target)
		if (!target.hasClass('is-selected')) {
			target.addClass('is-selected')
			// If target select button is not equal to the selected node's select button,
			// lower the opacity of the selected nodes select button.
			labelMask.toggleClass('is-selected', labelMask.is(target))
			this.$('.label-mask').not(target).removeClass('is-selected')
			this.delegateClickHandler()
			this.activeNode(this.$('.node').has(target))
		}
	},

	allowDrag: function() {
		let tree = this.$('.tree')
		if (tree?.hasClass('editable-no-drag')) {
			tree.addClass('editable').removeClass('editable-no-drag')
		}
	},

	// Called in order to 'select' or load a chosen node by clicking the 'Select'
	// button inside the .label-mask.
	select: function(e) {
		if (!this.options.propagateClick) {
			e.stopPropagation()
		}

		// if not addable, don't trigger anything
		if ($(e.currentTarget).closest('.addable').length == 0 || $(e.currentTarget).hasClass('has-no-tree-action')) {
			return
		}

		// Only select if button isn't disabled
		let target = $(e.currentTarget)
		let nodeId = target.closest('li.node').data('id')
		this.trigger('selected', nodeId)
	},

	applyCheckmark: function(node) {
		var node = node || this.$('.selected')
		var labelMask = node.find('.label-mask').addClass('button')
		this.checkmark = $('<span class="checkmark button"><span class="text">' + this.options.buttonContent + '</span></span>')
		new BusyButton({ el: this.checkmark, keepCheckmark: true }).instadone()
		labelMask.addClass('is-checked is-disabled').append(this.checkmark)
	},

	// Pass in the ID of a node to remove the checkmark and return it to its normal state
	removeCheckmark: function(id) {
		var node = this.$('.node[data-id='+ id +']')
		var checkmark = node.find('.checkmark')
		checkmark.remove()
		node.removeClass('is-loading is-done')
			.find('.label-mask')
			.removeClass('is-checked is-disabled')
	},

	// Return the id of the node with an active button.
	activeNode: function(node) {
		this.trigger('nodeActivated', node)
	}

})

export default HoverActions


HoverActions.__filename__ = 'components/hover-actions.js'
